import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DrawerOpenEvent } from '@theme/theme-drawer';

// These helpers use Shopify's whole-number quantities, without meter conversion.
export function wrappingQuantity(items, wrapId, mode, selected) {
  if (!selected) return 0;
  const products = items.filter((item) => Number(item.variant_id) !== wrapId);
  if (mode === 'per_order') return products.length ? 1 : 0;
  if (mode === 'per_line') return products.length;
  return products.reduce((sum, item) => sum + item.quantity, 0);
}

export function wrappingCount(cart, id) {
  return cart.items.reduce((sum, item) => sum + (Number(item.variant_id) === id ? item.quantity : 0), 0);
}

export async function reconcileWrapping(cart, id, wanted, request, readCart) {
  // Re-read keys from every response: discounts may split or re-key a line.
  // Using change.js for existing lines also lets Shopify check quantity limits.
  for (let attempt = 0; attempt < 25; attempt++) {
    const actual = wrappingCount(cart, id);
    if (actual === wanted) return cart;
    const lines = cart.items.filter((item) => Number(item.variant_id) === id);
    if (actual < wanted) {
      if (lines.length) {
        cart = await request('change', { id: lines[0].key, quantity: lines[0].quantity + wanted - actual });
      } else {
        await request('add', { items: [{ id, quantity: wanted }] });
        cart = await readCart();
      }
      if (wrappingCount(cart, id) !== wanted) {
        throw new Error('There is not enough gift wrapping available. Uncheck wrapping or reduce the product quantity.');
      }
    } else {
      const line = lines[lines.length - 1];
      cart = await request('change', { id: line.key, quantity: Math.max(0, line.quantity - (actual - wanted)) });
      if (wrappingCount(cart, id) >= actual) throw new Error('Gift wrapping could not be updated. Please try again.');
    }
  }
  throw new Error('Gift wrapping could not be updated. Please refresh the page.');
}

class ReferenceDrawerController {
  constructor() {
    this.tail = Promise.resolve();
    this.drafts = { message: null, note: null };
    this.wrapDraft = null;
    this.ownEvents = new WeakSet();
    this.readyForms = new WeakSet();
    this.lastQuantityEvent = -Infinity;
    document.addEventListener('input', (event) => this.onInput(event));
    document.addEventListener('change', (event) => this.onChange(event));
    document.addEventListener('submit', (event) => this.onSubmit(event));
    document.addEventListener('quantity-selector:update', (event) => {
      if (event.target.closest?.('#cart-drawer')) this.lastQuantityEvent = performance.now();
    });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (this.ownEvents.has(event) || !this.root?.dataset.wrapId) return;
      event.promise?.then(({ detail }) => {
        if (!detail?.didError) this.enqueue(() => this.reconcile()).catch((error) => this.showError(error));
      }).catch(() => {}); // The native cart displays its own quantity errors.
    });
    document.addEventListener(DrawerOpenEvent.eventName, (event) => {
      if (event.target.id === 'cart-drawer' && this.root?.dataset.wrapId) {
        this.enqueue(() => this.reconcile()).catch((error) => this.showError(error));
      }
    });
  }

  get root() { return document.querySelector('#cart-drawer .reference-cart-drawer'); }
  get base() { return (this.root?.dataset.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/'); }
  enqueue(action) {
    const job = this.tail.then(action);
    this.tail = job.catch(() => {});
    return job;
  }
  restore(extras) {
    for (const field of extras.querySelectorAll('[data-rcd-field]')) {
      const value = this.drafts[field.dataset.rcdField];
      if (value !== null && value !== undefined) field.value = value;
    }
    const checkbox = extras.querySelector('[data-rcd-wrap-toggle]');
    if (checkbox) {
      checkbox.disabled = checkbox.dataset.rcdAvailable !== 'true' && !checkbox.defaultChecked;
      if (this.wrapDraft !== null) checkbox.checked = this.wrapDraft;
    }
    queueMicrotask(() => this.updateExpressState());
  }
  onInput(event) {
    const field = event.target;
    if (!field.matches?.('[data-rcd-field]') || !this.root?.contains(field)) return;
    this.drafts[field.dataset.rcdField] = field.value;
    this.updateExpressState();
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.enqueue(async () => {
        await this.saveDrafts();
        this.status('Saved');
      }).catch((error) => this.showError(error));
    }, 250);
  }
  onChange(event) {
    if (!event.target.matches?.('[data-rcd-wrap-toggle]') || !this.root?.contains(event.target)) return;
    this.wrapDraft = event.target.checked;
    this.enqueue(() => this.reconcile()).catch((error) => this.showError(error));
  }
  async onSubmit(event) {
    const form = event.target;
    if (form.id !== 'cart-form' || !this.root?.contains(form)) return;
    if (this.readyForms.has(form)) { this.readyForms.delete(form); return; }
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    if (this.checkoutPending) return;
    this.checkoutPending = true;
    clearTimeout(this.saveTimer);
    for (const field of this.root.querySelectorAll('[data-rcd-field]')) {
      this.drafts[field.dataset.rcdField] = field.value;
    }
    try {
      await this.enqueue(() => this.reconcile());
      const current = this.root?.querySelector('#cart-form');
      if (!current) return; // Removing the last product can empty the cart.
      const checkout = this.root.querySelector('button[name="checkout"]');
      if (!checkout || checkout.disabled) return;
      // The API and native quantity component have already saved quantities.
      current.querySelectorAll('[name="updates[]"]').forEach((input) => { input.disabled = true; });
      this.readyForms.add(current);
      current.requestSubmit(checkout);
    } catch (error) {
      this.showError(error);
    } finally {
      this.checkoutPending = false;
    }
  }
  async waitForNativeQuantities() {
    // The native quantity event is debounced before its request enters the queue.
    const delay = 350 - (performance.now() - this.lastQuantityEvent);
    if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
    for (let pass = 0; pass < 5; pass++) {
      const pending = customElements.get('cart-items-component')?.pendingQuantityUpdates || [];
      if (!pending.length) return;
      await Promise.allSettled(pending);
    }
    throw new Error('Your quantity change is still being saved. Please try checkout again.');
  }
  async request(endpoint, body) {
    const response = await fetch(this.base + 'cart/' + endpoint + '.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    const result = await response.json();
    if (!response.ok || result.errors) {
      const message = result.description || result.message || result.errors;
      throw new Error(typeof message === 'string' ? message : 'Unable to update your cart. Please try again.');
    }
    return result;
  }
  async readCart() {
    const response = await fetch(this.base + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { 'Accept': 'application/json' } });
    if (!response.ok) throw new Error('Unable to read your cart. Please try again.');
    return response.json();
  }
  async saveDrafts() {
    const snapshot = { ...this.drafts };
    const body = {};
    if (snapshot.message !== null) body.attributes = { 'Gift message': snapshot.message };
    if (snapshot.note !== null) body.note = snapshot.note;
    if (!Object.keys(body).length) return null;
    const cart = await this.request('update', body);
    for (const key of Object.keys(snapshot)) {
      if (this.drafts[key] === snapshot[key]) this.drafts[key] = null;
    }
    this.updateExpressState();
    return cart;
  }
  updateExpressState() {
    const pending = Object.values(this.drafts).some((value) => value !== null);
    this.root?.querySelectorAll('.additional-checkout-buttons').forEach((element) => { element.inert = pending; });
  }
  lock(locked) {
    const root = this.root;
    if (!root) return;
    root.classList.toggle('rcd-saving', locked);
    if (locked) root.setAttribute('aria-busy', 'true');
    else root.removeAttribute('aria-busy');
    root.querySelectorAll('.cart-items__quantity-controls, .cart__ctas, .rcd-wrap-control').forEach((element) => { element.inert = locked; });
    if (!locked) this.updateExpressState();
  }
  async reconcile() {
    await this.waitForNativeQuantities();
    this.lock(true);
    this.status('Updating your cart…');
    try {
      let cart = await this.saveDrafts();
      const root = this.root;
      const id = Number(root?.dataset.wrapId);
      if (id) {
        cart ||= await this.readCart();
        const selected = this.wrapDraft ?? Boolean(root.querySelector('[data-rcd-wrap-toggle]')?.checked);
        const wanted = wrappingQuantity(cart.items, id, root.dataset.wrapMode, selected);
        if (wanted !== wrappingCount(cart, id)) {
          this.needsRender = true;
          cart = await reconcileWrapping(cart, id, wanted, (endpoint, body) => this.request(endpoint, body), () => this.readCart());
        }
      }
      if (this.needsRender) {
        await this.refreshView();
        this.needsRender = false;
      }
      this.wrapDraft = null;
      this.status('');
    } finally {
      this.lock(false);
    }
  }
  async refreshView() {
    const ids = [...new Set(['cart-drawer-section', ...Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (element) => element.dataset.sectionId)])].slice(0, 5);
    const cart = await this.request('update', { sections: ids, sections_url: window.location.pathname });
    const html = cart.sections?.['cart-drawer-section'];
    if (!html) throw new Error('The cart changed, but its preview could not refresh. Please refresh the page.');
    const target = this.root?.querySelector('cart-items-component');
    if (!target) throw new Error('Please refresh the page to see your updated cart.');
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
    this.ownEvents.add(event);
    target.dispatchEvent(event);
    try {
      sectionRenderer.abortRender('cart-drawer-section');
      await morphSection('cart-drawer-section', html, { mode: 'hydration', injectStylesheet: true });
      this.root?.querySelectorAll('cart-quantity-selector-component').forEach((element) => element.updateButtonStates?.());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'reference-cart-drawer', didError: false }
      });
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }
  status(message, error = false) {
    const element = this.root?.querySelector('.rcd-status');
    if (!element) return;
    element.textContent = message;
    element.toggleAttribute('data-error', error);
    if (error) element.focus();
  }
  showError(error) {
    this.lock(false);
    this.status(error.message || 'Unable to save your cart. Please try again.', true);
  }
}

const controller = new ReferenceDrawerController();
if (!customElements.get('reference-drawer-extras')) {
  customElements.define('reference-drawer-extras', class extends HTMLElement {
    connectedCallback() { controller.restore(this); }
  });
}
